import { create } from 'zustand';
import { rideAPI } from '../services/api';
import { normalizeRideStatus } from '../constants/ride-status';

interface Location {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Ride {
  id: string;
  ride_number: number;
  rider_id: string;
  driver_id?: string;
  service_id?: string;
  vehicle_type_id?: string;
  status: string;
  start_point: Location;
  end_point: Location;
  locations?: Location[];
  estimated_fare?: number;
  ride_fare?: number;
  payment_method?: string;
  payment_status?: string;
  driver?: any;
  rider?: any;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  origin_address?: string;
  destination_address?: string;
  currency_symbol?: string;
}

interface RideState {
  currentRide: Ride | null;
  rideHistory: Ride[];
  isLoading: boolean;
  createRide: (data: any) => Promise<Ride>;
  getCurrentRide: () => Promise<Ride | null>;
  getRideHistory: () => Promise<Ride[]>;
  cancelRide: (rideId: string, reason?: string) => Promise<void>;
  updateCurrentRide: (ride: Ride | null) => void;
  estimateFare: (data: any) => Promise<any>;
}


export const transformBackendRideToFrontend = (backendRide: any): Ride | null => {
  if (!backendRide) return null;


  const ride = backendRide.data || backendRide;

  return {
    id: ride.id?.toString() || ride.id,
    ride_number: ride.rideNumber || ride.ride_number || 0,
    rider_id: ride.riderId?.toString() || ride.rider_id?.toString() || ride.passengerId?.toString() || '',
    driver_id: ride.driverId?.toString() || ride.driver_id?.toString(),
    service_id: ride.serviceId?.toString() || ride.service_id?.toString(),
    vehicle_type_id: ride.vehicleTypeId?.toString() || ride.vehicle_type_id?.toString(),
    status: normalizeRideStatus(ride.status || ride.ride_status_config?.name || ride.rideStatusConfig?.name || ride.ride_status_id),
    start_point: ride.startPoint || {
      latitude: ride.originLatitude || 0,
      longitude: ride.originLongitude || 0,
      address: ride.originAddress || ride.origin_address,
    },
    end_point: ride.endPoint || {
      latitude: ride.destinationLatitude || 0,
      longitude: ride.destinationLongitude || 0,
      address: ride.destinationAddress || ride.destination_address,
    },
    locations: ride.locations || [
      {
        latitude: ride.originLatitude || ride.startPoint?.latitude || 0,
        longitude: ride.originLongitude || ride.startPoint?.longitude || 0,
        address: ride.originAddress || ride.origin_address,
      },
      {
        latitude: ride.destinationLatitude || ride.endPoint?.latitude || 0,
        longitude: ride.destinationLongitude || ride.endPoint?.longitude || 0,
        address: ride.destinationAddress || ride.destination_address,
      },
    ],
    estimated_fare: ride.estimatedFare || ride.estimated_fare,
    ride_fare: ride.rideFare || ride.ride_fare || ride.finalFare,
    payment_method: ride.paymentMethod || ride.payment_method,
    payment_status: ride.paymentStatus || ride.payment_status,
    driver: ride.driver,
    rider: ride.rider || ride.passenger,
    created_at: ride.createdAt || ride.created_at,
    started_at: ride.startedAt || ride.started_at,
    completed_at: ride.completedAt || ride.completed_at,
    cancelled_at: ride.cancelledAt || ride.cancelled_at,
    origin_address: ride.originAddress || ride.origin_address,
    destination_address: ride.destinationAddress || ride.destination_address,
    currency_symbol: ride.currencySymbol || ride.currency_symbol || '$',
  };
};

export const useRideStore = create<RideState>((set, get) => ({
  currentRide: null,
  rideHistory: [],
  isLoading: false,

  createRide: async (data: any) => {
    set({ isLoading: true });
    try {

      const backendData: any = {
        originLatitude: data.start_point?.latitude || data.originLatitude,
        originLongitude: data.start_point?.longitude || data.originLongitude,
        destinationLatitude: data.end_point?.latitude || data.destinationLatitude,
        destinationLongitude: data.end_point?.longitude || data.destinationLongitude,
        originAddress: data.start_point?.address || data.originAddress,
        destinationAddress: data.end_point?.address || data.destinationAddress,
        vehicleTypeId: data.vehicle_type_id || data.vehicleTypeId,
        serviceId: data.service_id || data.serviceId,
        estimatedFare: data.estimated_fare || data.estimatedFare,
        specialInstructions: data.special_instructions || data.specialInstructions,
      };

      if (data.entrance) {
        backendData.entrance = data.entrance;
      }

      if (data.stops && Array.isArray(data.stops) && data.stops.length > 0) {
        backendData.stops = data.stops.map((stop: any) => ({
          address: stop.address,
          notes: stop.notes || '',
          latitude: stop.latitude,
          longitude: stop.longitude,
        }));
      }

      console.log('Creating ride with data:', backendData);

      const response = await rideAPI.createRide(backendData);
      const transformedRide = transformBackendRideToFrontend(response);

      if (transformedRide) {
        set({ currentRide: transformedRide });
        return transformedRide;
      }

      throw new Error('Failed to create ride: invalid response');
    } catch (error: any) {
      console.error('Failed to create ride:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create ride';
      throw new Error(errorMessage);
    } finally {
      set({ isLoading: false });
    }
  },

  getCurrentRide: async () => {
    try {
      const response = await rideAPI.getActiveRide();
      const ride = response.data || response;

      if (ride) {
        const transformedRide = transformBackendRideToFrontend(ride);
        if (transformedRide) {
          set({ currentRide: transformedRide });
          return transformedRide;
        }
      }


      const existingRide = get().currentRide;
      return existingRide || null;
    } catch (error) {
      console.error('Failed to get current ride:', error);
      const existingRide = get().currentRide;
      return existingRide || null;
    }
  },

  getRideHistory: async () => {
    set({ isLoading: true });
    try {
      const response = await rideAPI.getRideHistory();
      const ridesList = Array.isArray(response) ? response : (response.data || []);
      const transformedRides = ridesList.map((ride: any) => transformBackendRideToFrontend(ride)).filter(Boolean) as Ride[];
      set({ rideHistory: transformedRides });
      return transformedRides;
    } catch (error) {
      console.error('Failed to get ride history:', error);
      return [];
    } finally {
      set({ isLoading: false });
    }
  },

  cancelRide: async (rideId: string, reason?: string) => {
    try {
      await rideAPI.cancelRide(rideId, reason || 'User cancelled');
      set({ currentRide: null });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel ride';
      throw new Error(errorMessage);
    }
  },

  updateCurrentRide: (ride: Ride | null) => {
    if (ride) {
      const transformedRide = transformBackendRideToFrontend(ride);
      set({ currentRide: transformedRide });
    } else {
      set({ currentRide: null });
    }
  },

  estimateFare: async (data: any) => {
    try {

      const backendData = {
        start_point: data.start_point || {
          latitude: data.originLatitude || data.origin?.latitude,
          longitude: data.originLongitude || data.origin?.longitude,
          address: data.origin?.address || '',
        },
        end_point: data.end_point || {
          latitude: data.destinationLatitude || data.destination?.latitude,
          longitude: data.destinationLongitude || data.destination?.longitude,
          address: data.destination?.address || '',
        },
        vehicle_type_id: String(data.vehicle_type_id || data.vehicleTypeId || data.vehicle_type_id || ''),
      };

      if (!backendData.start_point.latitude || !backendData.start_point.longitude) {
        throw new Error('start_point is required');
      }
      if (!backendData.end_point.latitude || !backendData.end_point.longitude) {
        throw new Error('end_point is required');
      }
      if (!backendData.vehicle_type_id) {
        throw new Error('vehicle_type_id is required');
      }

      return await rideAPI.estimateFare(backendData);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to estimate fare';
      throw new Error(errorMessage);
    }
  },
}));

